import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import {
  Cell,
  Column,
  Content,
  DialogContainer,
  Flex,
  Grid,
  Heading,
  IllustratedMessage,
  Item,
  ListBox,
  ProgressCircle,
  Row,
  TableBody,
  TableHeader,
  TableView,
  View,
} from '@adobe/react-spectrum';
import { CircleFill } from 'react-bootstrap-icons';
import NotFound from '@spectrum-icons/illustrations/NotFound';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDependency } from '../../../contexts/DependencyProvider';
import { useViewInfo } from '../../../hooks/UseViewInfo';
import { useDeviceDetection } from '../../../hooks/UseDeviceDetection';
import { GetSchedulerSettingsRequest, SettingsItemType } from '../../../services/soap/scheduler/requests/GetSchedulerSettingsRequest';
import { ListIssueRequest } from '../../../services/soap/scheduler/requests/ListIssueRequest';
import { GetIssueRequest } from '../../../services/soap/scheduler/requests/GetIssueRequest';
import { UpdateIssueRequest } from '../../../services/soap/scheduler/requests/UpdateIssueRequest';
import { DeleteIssueRequest } from '../../../services/soap/scheduler/requests/DeleteIssueRequest';
import { TaskTemplateListRequest } from '../../../services/soap/scheduler/requests/TaskTemplateListRequest';
import { ListUserRequest } from '../../../services/soap/access/requests/ListUserRequest';
import { statusColor } from '../../../utils/helperUtil';
import { FPADataFactory, FPADataTypes } from '../../../infra/protected/FPA/FPAData';
import { ToastQueue } from '@adobe/react-spectrum';
import FilterComponent from './components/FilterComponent';
import HeaderComponent from './components/HeaderComponent';
import CardComponent from './components/CardComponent';
import CardComponentMobile from './components/CardComponentMobile';
import IssueDetailsView from './components/IssueDetailsView';
import IssueDetailsViewMobile from './components/IssueDetailsViewMobile';
import IssueTemplatePickerDialog from './components/IssueTemplatePickerDialog';
import CreateIssueDialog from './components/CreateIssueDialog';
import styles from './Issue.module.css';

type IssueRow = {
  id: string;
  ident: string;
  title: string;
  entryTypeName: string;
  entryTypeColor?: string;
  userName: string;
  status: string;
  statusName: string;
  statusColor: string;
  deadLine: string;
  created: string;
  folderName: string;
  folderId: string;
  projectName: string;
  projectId: string;
  activityName: string;
  activityId: string;
  refId: string;
  priority?: any;
  solver?: string;
  typeId: string;
  solverId: string;
  dateFrom?: string;
  dateTo?: string;
  createdBy?: string;
  enteredBy?: string;
  creatorName?: string;
  userLogin?: string;
  garantName?: string;
};

type IssueTemplate = {
  id: string;
  acl?: string;
  name: string;
  taskTitle?: string;
  typeOfWork?: string;
  status?: string;
  priority?: string;
  reminder?: string;
  solver?: string;
  solverName?: string;
  repeat?: {
    repeatMode?: string;
    validFrom?: string;
    validTo?: string;
    repeatEvery?: string;
    repeatDay?: string;
    repeatMonth?: string;
    repeatWeekDay?: string;
    repeatWeekCount?: string;
    repeatWeekDayList?: string;
    timeFrom?: string;
    timeTo?: string;
    useOffset?: string;
    offset?: string;
    limit?: string;
  };
};

const PAGE_SIZE = 100;

const Loader = () => (
  <Flex width="100%" justifyContent="center" marginTop={10}>
    <ProgressCircle aria-label="Loading" isIndeterminate />
  </Flex>
);

const normalizeResponseItems = (payload: any): any[] => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (typeof payload === 'object') {
    const values = Object.values(payload);
    return values.length > 0 ? values : [payload];
  }
  return [];
};

export function Issue() {
  const { t } = useTranslation('issue');
  const _tf = useCallback((key: string) => t(`issue_filters.${key}`, { ns: 'issue' }), [t]);
  const _tt = useCallback((key: string) => t(`issue_table_column.${key}`, { ns: 'issue' }), [t]);
  const _m = useCallback((key: string) => t(`issue_messages.${key}`, { ns: 'issue' }), [t]);
  const { isMobile } = useViewInfo();
  const deviceInfo = useDeviceDetection(isMobile);
  /** Same rule as Communication: tablet/iPad landscape uses desktop split; portrait stays compact. */
  const shouldUseDesktopLayout =
    !deviceInfo.shouldUseMobileLayout() ||
    ((deviceInfo.isTablet || deviceInfo.isIPad) &&
      deviceInfo.orientation === 'landscape');
  const { store, schedulerService, accessService } = useDependency();
  const [params] = useSearchParams();
  const [showLoader, setShowLoader] = useState(true);
  const [showLoaderDetail, setShowLoaderDetail] = useState(true);
  const [rowsData, setRowsData] = useState<IssueRow[]>([]);
  const [columnsData, setColumnsData] = useState<any[]>([]);
  const [pageView, setPageView] = useState<string>('Full');
  const [selectedKeys, setSelectedKeys] = useState<Set<any>>(new Set());
  const [issueStatusList, setIssueStatusList] = useState<any[]>([]);
  const [showCards, setShowCards] = useState<boolean>(!params.has('id'));
  const [selectedItem, setSelectedItem] = useState<any | null>(null);
  const [showFilter, setShowFilter] = useState<boolean>(false);
  const [isStatusUpdating, setIsStatusUpdating] = useState(false);
  const [issueTypes, setIssueTypes] = useState<Array<{ id: string; name: string; formId?: string; formName?: string; color?: string }>>([]);
  const [userData, setUserData] = useState<Array<{ id: string; name: string }>>([]);
  const [issueTemplates, setIssueTemplates] = useState<IssueTemplate[]>([]);
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<IssueTemplate | null>(null);
  const [viewCounts, setViewCounts] = useState<Record<string, number>>({ issues: 0, my_issues: 0 });
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const offSetLimit = useRef<number>(0);
  const totalProductListRec = useRef<number>(0);
  const queryParams = useRef<any>({ FILTER: { COL: [] } });
  const hasInitializedRef = useRef(false);
  const issueTypeMapRef = useRef<Record<string, string>>({});
  const issueTypeSettingsMapRef = useRef<Record<string, { id: string; name: string; formId?: string; formName?: string; color?: string }>>({});
  const userDataRef = useRef<Array<{ id: string; name: string }>>([]);
  const isDeepLinked = params.has('id');

  const detailStyle = { backgroundColor: 'white' } as const;
  const cardStyle = { backgroundColor: '#f5f6f9' } as const;
  const NoResultsMessage = () => (
    <Flex
      direction="row"
      width="100%"
      height="100%"
      minHeight="100%"
      alignItems="center"
      justifyContent="center"
      UNSAFE_style={{ minHeight: 'calc(100vh - 220px)' }}
    >
      <IllustratedMessage>
        <NotFound />
        <Heading>{_m('no_issues_found')}</Heading>
        <Content>{_m('try_another_filter_combination')}</Content>
      </IllustratedMessage>
    </Flex>
  );

  const issueTypeMap = useMemo(
    () =>
      issueTypes.reduce<Record<string, string>>((acc, item) => {
        acc[item.id] = item.name;
        return acc;
      }, {}),
    [issueTypes]
  );

  const issueTypeSettingsMap = useMemo(
    () =>
      issueTypes.reduce<Record<string, { id: string; name: string; formId?: string; formName?: string; color?: string }>>((acc, item) => {
        acc[item.id] = item;
        return acc;
      }, {}),
    [issueTypes]
  );

  const issueStatusMap = useMemo(
    () =>
      issueStatusList.reduce<Record<string, any>>((acc, item) => {
        acc[String(item.ident)] = item;
        return acc;
      }, {}),
    [issueStatusList]
  );

  useEffect(() => {
    issueTypeMapRef.current = issueTypeMap;
  }, [issueTypeMap]);

  useEffect(() => {
    issueTypeSettingsMapRef.current = issueTypeSettingsMap;
  }, [issueTypeSettingsMap]);

  useEffect(() => {
    userDataRef.current = userData;
  }, [userData]);

  const getFormSettings = useCallback(async () => {
    setColumnsData([
      { name: _tt('type'), key: 'entryTypeName', width: 170 },
      { name: _tt('title'), key: 'title', width: 400 },
      { name: _tt('solver'), key: 'userName', width: 200 },
      { name: _tt('created'), key: 'deadLine', width: 200 },
      { name: _tt('state'), key: 'statusName', width: 200 },
    ]);
  }, [_tt]);

  const getIssueStatusList = useCallback(async () => {
    const statusArray: any[] = [];
    const res: any = await schedulerService.getSchedulerSettings(
      new GetSchedulerSettingsRequest(store.Server, store.SessionId, '1', SettingsItemType.ISSUE_ITEM_TYPE, 50, 0)
    );
    if (res.result === 'OK' && res.ITEMS.length > 0) {
      res.ITEMS.forEach((status: any) => {
        const { ident, title, color } = status.ITEM;
        statusArray.push({ ident, title, color: statusColor(color) });
      });
      statusArray.unshift({ ident: 'All states', title: '', color: 'black' });
      setIssueStatusList(statusArray);
      return statusArray;
    }
    return [];
  }, [schedulerService, store.Server, store.SessionId]);

  const getUserData = useCallback(async () => {
    const userList = await accessService.listUser(new ListUserRequest(store.Server, store.SessionId, true, 500));
    if (userList.result === 'OK') {
      const formatted = userList.USERS.map(({ USER }: any) => ({
        id: String(USER.id),
        name:
          `${USER.name1 || ''} ${USER.name3 || ''}`.trim() ||
          String(USER.name || USER.login || USER.username || USER.email || USER.id || '').trim(),
      }));
      setUserData(formatted);
      return formatted;
    }
    return [];
  }, [accessService, store.Server, store.SessionId]);

  const getIssueTypes = useCallback(async () => {
    const response: any = await schedulerService.getSchedulerSettings(
      new GetSchedulerSettingsRequest(store.Server, store.SessionId, '4', SettingsItemType.ISSUE_ITEM_TYPE, 50, 0)
    );
    if (response?.result === 'OK') {
      const types = normalizeResponseItems(response.ITEMS || response.ITEM)
        .map((entry: any) => {
          const item = entry?.ITEM || entry;
          return {
            id: String(item?.ident || item?.id || item?.taskTemplateId || ''),
            name: String(item?.title || item?.name || item?.entryTypeName || 'Unknown'),
            formId: item?.preTaskChecklistForm ? String(item.preTaskChecklistForm) : undefined,
            formName: item?.preTaskChecklistFormName ? String(item.preTaskChecklistFormName) : undefined,
            color: item?.color ? statusColor(item.color) : undefined,
          };
        })
        .filter((item: any) => item.id);
      setIssueTypes(types);
      return types;
    }
    return [];
  }, [schedulerService, store.Server, store.SessionId]);

  const getIssueTemplates = useCallback(async () => {
    const response: any = await schedulerService.taskTemplateList(
      new TaskTemplateListRequest(store.Server, store.SessionId, 100, 'i', 0)
    );
    const responseArray = Array.isArray(response) ? response : [response];
    const root = responseArray.find((entry: any) => entry?.result || entry?.count) || responseArray[0];
    const templateItems = responseArray.filter((entry: any) => entry?.ITEM);

    if (root?.result === 'OK' || templateItems.length > 0) {

      const templates = templateItems
        .map((entry: any) => {
          const item = entry?.ITEM || entry;
          const solverId = String(item?.solver || '');
          const solverName =
            userDataRef.current.find((user) => String(user.id) === solverId)?.name ||
            solverId ||
            t('issue_template_picker.unassigned', { ns: 'issue' });
          return {
            id: String(item?.id || item?.ident || ''),
            acl: String(item?.acl || ''),
            name: String(item?.name || ''),
            taskTitle: String(item?.taskTitle || item?.name || ''),
            typeOfWork: String(item?.typeOfWork || ''),
            status: String(item?.status || ''),
            priority: String(item?.priority || '0'),
            reminder: String(item?.reminder || '0'),
            solver: solverId,
            solverName,
            repeat: item?.REPEAT
              ? {
                  repeatMode: String(item.REPEAT.repeatMode || ''),
                  validFrom: String(item.REPEAT.validFrom || ''),
                  validTo: String(item.REPEAT.validTo || ''),
                  repeatEvery: String(item.REPEAT.repeatEvery || ''),
                  repeatDay: String(item.REPEAT.repeatDay || ''),
                  repeatMonth: String(item.REPEAT.repeatMonth || ''),
                  repeatWeekDay: String(item.REPEAT.repeatWeekDay || ''),
                  repeatWeekCount: String(item.REPEAT.repeatWeekCount || ''),
                  repeatWeekDayList: String(item.REPEAT.repeatWeekDayList || ''),
                  timeFrom: String(item.REPEAT.timeFrom || ''),
                  timeTo: String(item.REPEAT.timeTo || ''),
                  useOffset: String(item.REPEAT.useOffset || ''),
                  offset: String(item.REPEAT.offset || ''),
                  limit: String(item.REPEAT.limit || ''),
                }
              : undefined,
          };
        })
        .filter((item: IssueTemplate) => item.id);
      setIssueTemplates(templates);
      return templates;
    }
    setIssueTemplates([]);
    return [];
  }, [schedulerService, store.Server, store.SessionId, t]);

  const buildPlainUserFilter = useCallback((solverId?: string) => {
    const userId = String(solverId || store.UserId || '').trim();
    if (!userId) return undefined;
    return [{ id: userId }];
  }, [store.UserId]);

  const mapFiltersToRequest = useCallback((filters: { COL: any[] }) => {
    const requestParams: {
      entryType?: string;
      solutionStatus?: string;
      USERFILTER?: Array<{ id: string; asGarant?: string; asCreator?: string }>;
      taskView?: string;
    } = {};

    filters.COL.forEach((filter) => {
      if (filter.name === 'customState') {
        const first = filter.value instanceof Set ? Array.from(filter.value)[0] : filter.value;
        if (first && String(first) !== _tf('all_types')) {
          requestParams.entryType = String(first);
        }
      }
      if (filter.name === 'user') {
        const first = filter.value instanceof Set ? Array.from(filter.value)[0] : filter.value;
        if (first && String(first) !== 'All users') {
          requestParams.USERFILTER = buildPlainUserFilter(String(first));
        }
      }
      if (filter.name === 'standardStatus') {
        const first = filter.value instanceof Set ? Array.from(filter.value)[0] : filter.value;
        if (first && String(first) !== '0' && String(first) !== 'All states') {
          requestParams.solutionStatus = String(first);
        }
      }
      if (filter.name === 'view') {
        requestParams.taskView = String(filter.value);
      }
    });

    return requestParams;
  }, [_tf, buildPlainUserFilter]);

  const getIssueListSearchParams = useCallback(() => ({
    rangeStart: new Date(new Date().getFullYear() - 50, new Date().getMonth(), new Date().getDate(), 0, 0, 0, 0),
    rangeEnd: new Date(new Date().getFullYear() + 50, new Date().getMonth(), new Date().getDate() + 1, 0, 0, 0, 0),
    anyTime: 'false',
    includePast: 'true',
    standardStatus: '0',
    orderBy1Desc: 'true',
    orderBy1Field: 'created',
    dateOfInterest: '0',
    groupUsers: '1',
    userFilter: '1',
    USERFILTER: buildPlainUserFilter(),
  }), [buildPlainUserFilter]);

  const applyIssueViewFilters = useCallback((params: any, requestParams: any) => {
    const sessionUserId = String(store.UserId || '').trim();
    const selectedUserIds =
      Array.isArray(requestParams.USERFILTER) && requestParams.USERFILTER.length > 0
        ? requestParams.USERFILTER.map((item: any) => String(item?.id || '').trim()).filter(Boolean)
        : [];

    if (requestParams.taskView === 'my_issues') {
      const ids = selectedUserIds.length > 0 ? selectedUserIds : sessionUserId ? [sessionUserId] : [];
      if (ids.length === 0) {
        params.userFilter = '1';
        delete params.USERFILTER;
        return params;
      }
      params.userFilter = '1';
      params.USERFILTER = ids.flatMap((id: string) => [
        { id },
        { id, asGarant: '1' },
        { id, asCreator: '1' },
      ]);
      return params;
    }

    if (selectedUserIds.length > 0) {
      params.userFilter = '1';
      params.USERFILTER = selectedUserIds.map((id: string) => ({ id }));
      return params;
    }

    params.userFilter = '1';
    delete params.USERFILTER;
    return params;
  }, [store.UserId]);

  const mapIssueRow = useCallback(
    (raw: any, users: any[]) => {
      const ident = String(raw?.ident || raw?.id || '');
      const garant = raw?.GARANT?.name || raw?.GARANT?.USER?.name || '';
      const userNode = raw?.USER || {};
      const userId = String(raw?.userId || userNode?.id || raw?.solver || '');
      const statusId = String(raw?.solutionStatus || raw?.status || '');
      const created = String(raw?.created || raw?.modified || '');
      const dateFrom = String(raw?.dateFrom || '');
      const dateTo = String(raw?.dateTo || '');
      const fallbackUserName =
        String(raw?.userName || userNode?.name || '') ||
        users.find((u: any) => String(u.id) === userId)?.name ||
        String(raw?.solverName || '');
      const solverName = String(garant || fallbackUserName || '');
      const status = issueStatusMap[statusId];
      const typeId = String(raw?.taskTemplateId || raw?.entryType || raw?.type || '');
      const latestTypeName = issueTypeMapRef.current[typeId] || '';
      const latestTypeColor = issueTypeSettingsMapRef.current[typeId]?.color || undefined;
      const createdBy = String(raw?.createdBy || '');
      const entererId = String(raw?.entererId || '');
      const createdByUserName = users.find((u: any) => String(u.id) === createdBy)?.name || '';
      const enteredByUserName = users.find((u: any) => String(u.id) === entererId)?.name || '';
      const creatorName =
        createdByUserName ||
        enteredByUserName ||
        String(raw?.createdByName || raw?.modifiedByName || '') ||
        '';
      const displayDate = dateTo || dateFrom || created;
      const createdDisplay = created ? moment(created).format('DD.MM.YYYY') : '';

      return {
        id: ident,
        ident,
        title: String(raw?.title || raw?.name || raw?.subject || raw?.refId || 'Untitled issue'),
        entryTypeName: String(raw?.entryTypeName || raw?.typeName || latestTypeName || ''),
        entryTypeColor: latestTypeColor,
        userName: fallbackUserName,
        solver: solverName,
        solverId: userId,
        status: statusId,
        statusName: status?.title || statusId,
        statusColor: status?.color || statusColor(raw?.color) || '#B0BEC5',
        deadLine: displayDate ? moment(displayDate).format('DD.MM.YYYY') : '',
        createdDisplay,
        created,
        dateFrom,
        dateTo,
        folderName: String(raw?.folderName || ''),
        folderId: String(raw?.folderId || '0'),
        projectName: String(raw?.projectName || ''),
        projectId: String(raw?.projectId || '0'),
        activityName: String(raw?.activityName || ''),
        activityId: String(raw?.activityId || '0'),
        refId: String(raw?.refId || raw?.folderRefId || raw?.projectRefId || raw?.activityRefId || ''),
        priority: raw?.priority ?? null,
        typeId,
        project: String(raw?.projectId || '0'),
        createdBy,
        enteredBy: entererId,
        creatorName,
        userLogin: String(userNode?.name || ''),
        garantName: String(garant || ''),
      };
    },
    [issueStatusMap]
  );

  const applyClientSearchFilters = useCallback((items: IssueRow[], filters: { COL: any[] }) => {
    const nameFilter = String(filters.COL.find((filter) => filter.name === 'contains')?.value || '').trim().toLowerCase();
    const docNumberFilter = String(filters.COL.find((filter) => filter.name === 'docNumber')?.value || '').trim().toLowerCase();

    if (!nameFilter && !docNumberFilter) {
      return items;
    }

    return items.filter((item) => {
      const matchesName =
        !nameFilter ||
        [
          item.title,
          item.folderName,
          item.projectName,
          item.activityName,
          item.userName,
          item.solver,
          item.entryTypeName,
        ]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(nameFilter));

      const matchesDocNumber =
        !docNumberFilter ||
        [item.refId, item.ident]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(docNumberFilter));

      return matchesName && matchesDocNumber;
    });
  }, []);

  const loadIssueData = useCallback(async () => {
    const requestParams = mapFiltersToRequest(queryParams.current.FILTER);
    const params: any = applyIssueViewFilters({ ...getIssueListSearchParams(), ...requestParams }, requestParams);
    const users = userData.length > 0 ? userData : await getUserData();
    const request = new ListIssueRequest(store.Server, store.SessionId, {
      ...params,
      limit: PAGE_SIZE,
      offset: offSetLimit.current,
    });

    const response: any = await schedulerService.listIssue(
      request
    );

    if (response?.result !== 'OK') {
      setRowsData([]);
      return [];
    }

    const mapped = normalizeResponseItems(response.ITEMS || response.ITEM).map((entry: any) =>
      mapIssueRow(entry?.ITEM || entry, users)
    );
    const filtered = applyClientSearchFilters(mapped, queryParams.current.FILTER);

    totalProductListRec.current = Number(response.count || mapped.length);
    setViewCounts((prev) => ({
      ...prev,
      [requestParams.taskView || 'issues']: filtered.length,
    }));
    setRowsData(filtered);
    setHasMore((!queryParams.current.FILTER.COL.some((filter: any) => filter.name === 'contains' || filter.name === 'docNumber')) && offSetLimit.current + mapped.length < totalProductListRec.current);
    return filtered;
  }, [applyClientSearchFilters, applyIssueViewFilters, getIssueListSearchParams, getUserData, mapFiltersToRequest, mapIssueRow, schedulerService, store.Server, store.SessionId, userData]);

  const listIssueList: any = {
    items: rowsData,
    isLoading: isLoadingMore,
    loadingState: isLoadingMore ? 'loadingMore' : 'idle',
    async loadMore() {
      if (!hasMore) return;
      setIsLoadingMore(true);
      offSetLimit.current += PAGE_SIZE;
      try {
        const requestParams = mapFiltersToRequest(queryParams.current.FILTER);
        const params: any = applyIssueViewFilters({ ...getIssueListSearchParams(), ...requestParams }, requestParams);
        const users = userData.length > 0 ? userData : await getUserData();
        const response: any = await schedulerService.listIssue(
          new ListIssueRequest(store.Server, store.SessionId, {
            ...params,
            limit: PAGE_SIZE,
            offset: offSetLimit.current,
          })
        );
        if (response?.result === 'OK') {
          const mapped = normalizeResponseItems(response.ITEMS || response.ITEM).map((entry: any) =>
            mapIssueRow(entry?.ITEM || entry, users)
          );
          const filtered = applyClientSearchFilters(mapped, queryParams.current.FILTER);
          setRowsData((prev) => [...prev, ...filtered.filter((item: any) => !prev.some((row) => row.id === item.id))]);
          setHasMore((!queryParams.current.FILTER.COL.some((filter: any) => filter.name === 'contains' || filter.name === 'docNumber')) && offSetLimit.current + mapped.length < Number(response.count || 0));
        }
      } finally {
        setIsLoadingMore(false);
      }
    },
    async reload() {
      offSetLimit.current = 0;
      await reloadIssues();
    },
  };

  const getIssueDetail = useCallback(
    async (issueId: string) => {
      if (!issueId) return;
      setShowLoaderDetail(true);
      try {
        const response: any = await schedulerService.getIssue(
          new GetIssueRequest(store.Server, store.SessionId, issueId, String(store.UserId))
        );
        if (response?.result === 'OK') {
          const detail = response.ITEM || {};
          const users = userDataRef.current;
          const typeSettingsMap = issueTypeSettingsMapRef.current;
          const mapped = mapIssueRow(detail, users);
          const activityId = Number(detail?.activityId || 0);
          const projectId = Number(detail?.projectId || 0);
          const typeSettings = typeSettingsMap[String(detail?.entryType || mapped.typeId || '')];
          const customFormId = String(detail?.preChecklistForm || typeSettings?.formId || '').trim();
          const customFormValuesId = String(detail?.preChecklistFormValues || '').trim();
          setSelectedItem({
            ...detail,
            ...mapped,
            description: String(detail?.description || ''),
            comments: String(detail?.comments || ''),
            htmlText: String(detail?.htmlText || ''),
            remoteId: String(detail?.ident || issueId),
            remoteAcl: String(detail?.aclh || ''),
            customFormId: customFormId || undefined,
            customFormValuesId: customFormValuesId || undefined,
            selectedFormContext: activityId > 0
              ? FPADataFactory.createActivity(
                  activityId,
                  String(detail?.activityName || mapped.title),
                  '',
                  '',
                  0,
                  mapped.statusName,
                  mapped.statusColor,
                  '/assets/icons/group-4022.svg',
                  0,
                  mapped.entryTypeName || '',
                  projectId > 0 ? projectId : undefined,
                  FPADataTypes.PROJECT
                )
              : null,
          });
          setSelectedKeys(new Set([issueId]));
        }
      } finally {
        setShowLoaderDetail(false);
      }
    },
    [mapIssueRow, schedulerService, store.Server, store.SessionId, store.UserId]
  );

  const reloadIssues = useCallback(async () => {
    setShowLoader(true);
    setShowLoaderDetail(true);
    const data = await loadIssueData();
    setShowLoader(false);
    if (data.length > 0) {
      const targetId = params.get('id') && data.some((item: any) => item.id === params.get('id')) ? params.get('id')! : data[0].id;
      await getIssueDetail(targetId);
    } else {
      setSelectedItem(null);
      setSelectedKeys(new Set());
      setShowLoaderDetail(false);
    }
  }, [getIssueDetail, loadIssueData, params]);

  const handleAddNew = useCallback(async () => {
    const templates = issueTemplates.length > 0 ? issueTemplates : await getIssueTemplates();
    if (!templates || templates.length === 0) {
      ToastQueue.negative(_m('no_issue_templates_available'), { timeout: 2000 });
      return;
    }
    setIssueTemplates(templates);
    setShowTemplatePicker(true);
  }, [_m, getIssueTemplates, issueTemplates]);

  const handleTemplateSelect = useCallback((template: IssueTemplate) => {
    setSelectedTemplate(template);
    setShowTemplatePicker(false);
    setShowAddDialog(true);
  }, []);

  const handleAddNewClose = useCallback(() => {
    setShowAddDialog(false);
    setSelectedTemplate(null);
  }, []);

  const handleRefreshAddNew = useCallback(async (issue: any) => {
    setShowAddDialog(false);
    setSelectedTemplate(null);
    await reloadIssues();
    if (issue?.ident || issue?.id) {
      await getIssueDetail(String(issue.ident || issue.id));
    }
  }, [getIssueDetail, reloadIssues]);

  useEffect(() => {
    if (hasInitializedRef.current) {
      return;
    }
    hasInitializedRef.current = true;

    (async () => {
      setShowLoader(true);
      setShowLoaderDetail(true);
      queryParams.current = { FILTER: { COL: [] } };
      setPageView('Full');
      await Promise.all([getIssueStatusList(), getIssueTypes(), getUserData(), getFormSettings()]);
      setShowFilter(true);
      await reloadIssues();
    })();
  }, [getFormSettings, getIssueStatusList, getIssueTypes, getUserData, reloadIssues]);

  const handleViewChange = (view: string) => setPageView(view);
  const reloadTasks = useCallback(async () => {
    offSetLimit.current = 0;
    setShowCards(true);
    await reloadIssues();
  }, [reloadIssues]);

  const handleStatusChange = useCallback(async (statusIdent: string) => {
    if (!selectedItem?.ident) {
      return;
    }

    const issueId = String(selectedItem.ident);
    const previousStatus = String(selectedItem.status || selectedItem.solutionStatus || '');
    const previousStatusName = selectedItem.statusName;
    const previousStatusColor = selectedItem.statusColor;
    const selectedStatus = issueStatusList.find((item: any) => String(item.ident) === String(statusIdent));
    setIsStatusUpdating(true);

    setSelectedItem((prev: any) =>
      prev
        ? {
            ...prev,
            status: String(statusIdent),
            solutionStatus: String(statusIdent),
            statusName: selectedStatus?.title || prev.statusName,
            statusColor: selectedStatus?.color || prev.statusColor,
          }
        : prev
    );

    setRowsData((prev) =>
      prev.map((row) =>
        row.id === issueId
          ? {
              ...row,
              status: String(statusIdent),
              solutionStatus: String(statusIdent),
              statusName: selectedStatus?.title || row.statusName,
              statusColor: selectedStatus?.color || row.statusColor,
            }
          : row
      )
    );

    const payload = {
      ...selectedItem,
      ident: issueId,
      status: String(statusIdent),
      solutionStatus: String(statusIdent),
    };

    try {
      const response: any = await schedulerService.updateIssue(
        new UpdateIssueRequest(store.Server, store.SessionId, payload)
      );

      if (response?.result === 'OK') {
        ToastQueue.positive(_m('issue_updated'), { timeout: 1200 });
        return;
      }

      setSelectedItem((prev: any) =>
        prev
          ? {
              ...prev,
              status: previousStatus,
              solutionStatus: previousStatus,
              statusName: previousStatusName,
              statusColor: previousStatusColor,
            }
          : prev
      );

      setRowsData((prev) =>
        prev.map((row) =>
          row.id === issueId
            ? {
                ...row,
                status: previousStatus,
                solutionStatus: previousStatus,
                statusName: previousStatusName,
                statusColor: previousStatusColor,
              }
            : row
        )
      );

      ToastQueue.negative(response?.EXCEPTION?.message || _m('unable_to_update_issue'), { timeout: 2000 });
    } catch (error) {
      setSelectedItem((prev: any) =>
        prev
          ? {
              ...prev,
              status: previousStatus,
              solutionStatus: previousStatus,
              statusName: previousStatusName,
              statusColor: previousStatusColor,
            }
          : prev
      );

      setRowsData((prev) =>
        prev.map((row) =>
          row.id === issueId
            ? {
                ...row,
                status: previousStatus,
                solutionStatus: previousStatus,
                statusName: previousStatusName,
                statusColor: previousStatusColor,
              }
            : row
        )
      );

      ToastQueue.negative(_m('unable_to_update_issue'), { timeout: 2000 });
    } finally {
      setIsStatusUpdating(false);
    }
  }, [_m, issueStatusList, schedulerService, selectedItem, store.Server, store.SessionId]);

  const handleIssueUpdate = useCallback(async (nextValues: { docNo?: string; sendTo?: string; folderId?: string; projectId?: string; activityId?: string }) => {
    if (!selectedItem?.ident) {
      return false;
    }

    const nextDocNo = String(nextValues.docNo || '').trim();
    const nextSendTo = String(nextValues.sendTo || '').trim();
    const nextFolderId = String(nextValues.folderId || selectedItem?.folderId || '').trim();
    const nextProjectId = String(nextValues.projectId || selectedItem?.projectId || '').trim();
    const nextActivityId = String(nextValues.activityId || selectedItem?.activityId || '').trim();
    const nextStatus = String(selectedItem.status || selectedItem.solutionStatus || '').trim();
    const nextSendToName =
      userData.find((user) => String(user.id) === nextSendTo)?.name ||
      selectedItem?.garantName ||
      selectedItem?.solver ||
      '';
    const payload = {
      ...selectedItem,
      ident: String(selectedItem.ident),
      refId: nextDocNo,
      solutionStatus: nextStatus || String(selectedItem.solutionStatus || ''),
      userId: nextSendTo || String(selectedItem.userId || selectedItem.solverId || store.UserId || ''),
      USER: { id: nextSendTo || String(selectedItem.userId || selectedItem.solverId || store.UserId || '') },
      folderId: nextFolderId,
      projectId: nextProjectId,
      activityId: nextActivityId,
    };

    const response: any = await schedulerService.updateIssue(
      new UpdateIssueRequest(store.Server, store.SessionId, payload)
    );

    if (response?.result === 'OK') {
      setSelectedItem((prev: any) =>
        prev
            ? {
                ...prev,
                refId: nextDocNo,
                status: nextStatus || prev.status,
                solutionStatus: nextStatus || prev.solutionStatus,
                solverId: nextSendTo || prev.solverId,
                solver: nextSendToName || prev.solver,
                userId: nextSendTo || prev.userId,
                userName: nextSendToName || prev.userName,
                folderId: nextFolderId,
                projectId: nextProjectId,
                activityId: nextActivityId,
              }
            : prev
        );

      setRowsData((prev) =>
        prev.map((row) =>
          row.id === String(selectedItem.ident)
            ? {
                ...row,
                refId: nextDocNo,
                status: nextStatus || row.status,
                solutionStatus: nextStatus || row.status,
                solverId: nextSendTo || row.solverId,
                solver: nextSendToName || row.solver,
                userName: nextSendToName || row.userName,
                folderId: nextFolderId,
                projectId: nextProjectId,
                activityId: nextActivityId,
              }
            : row
        )
      );

      // Context names (folderName/projectName/activityName) are server-derived.
      // When the context changed, refresh once so the detail header + breadcrumbs
      // reflect the new values without requiring a full page reload.
      const contextChanged =
        String(selectedItem?.folderId || '') !== nextFolderId ||
        String(selectedItem?.projectId || '') !== nextProjectId ||
        String(selectedItem?.activityId || '') !== nextActivityId;
      if (contextChanged) {
        try {
          await getIssueDetail(String(selectedItem?.ident || ''));
        } catch {
          // Best-effort; keep optimistic IDs even if refresh fails.
        }
      }

      return true;
    }

    ToastQueue.negative(response?.EXCEPTION?.message || _m('unable_to_update_issue'), { timeout: 2000 });
    return false;
  }, [_m, schedulerService, selectedItem, store.Server, store.SessionId, store.UserId, userData, getIssueDetail]);

  const handleIssueDelete = useCallback(async () => {
    if (!selectedItem?.ident) {
      return;
    }

    const response: any = await schedulerService.deleteIssue(
      new DeleteIssueRequest(
        store.Server,
        store.SessionId,
        String(selectedItem.ident),
        false,
        String(selectedItem.headId || selectedItem.ident)
      )
    );

    if (response?.result !== 'OK') {
      ToastQueue.negative(response?.EXCEPTION?.message || _m('unable_to_delete_issue'), { timeout: 2000 });
      return;
    }

    ToastQueue.positive(_m('issue_deleted'), { timeout: 1500 });

    setSelectedItem(null);
    setSelectedKeys(new Set());
    setShowCards(true);
    await reloadIssues();
  }, [_m, reloadIssues, schedulerService, selectedItem, store.Server, store.SessionId]);

  const handleListRowClick = (rowId: any) => {
    const key = Array.from(rowId as Set<any>)[0];
    if (key) {
      if (!shouldUseDesktopLayout) {
        setShowCards(false);
      }
      void getIssueDetail(String(key));
    }
  };

  const handleTableRowClick = (rowId: any) => {
    const selectedRow =
      listIssueList.items.find((item: any) => item.id === rowId) ||
      rowsData.find((item: any) => item.id === rowId);

    if (!rowId) {
      return;
    }

    if (selectedKeys?.has?.(rowId)) {
      return;
    }

    setShowLoaderDetail(true);
    setSelectedKeys(new Set([rowId]));
    setPageView('Full');

    if (!selectedRow) {
      setSelectedItem(null);
      setSelectedKeys(new Set());
      return;
    }

    void getIssueDetail(String(selectedRow.id));
  };

  const filterStyle = { height: '100%' };

  return (
    <>
      {shouldUseDesktopLayout ? (
        !(isDeepLinked && !showCards) ? (
        <Grid
          areas={
            (deviceInfo.isTablet || deviceInfo.isIPad)
              ? pageView === 'Full'
                ? ['header header', 'filter filter', 'card detail']
                : ['header header', 'filter filter', 'detail detail']
              : pageView === 'Full'
                ? ['filter header header', 'filter card detail']
                : ['filter header header', 'filter detail detail']
          }
          columns={{
            base: ((deviceInfo.isTablet || deviceInfo.isIPad) && deviceInfo.orientation === 'landscape')
              ? ['35%', '65%']
              : ['245px', '28%', 'auto'],
            L: ((deviceInfo.isTablet || deviceInfo.isIPad) && deviceInfo.orientation === 'landscape')
              ? ['35%', '65%']
              : ['245px', '28%', 'auto']
          }}
          rows={(deviceInfo.isTablet || deviceInfo.isIPad) ? ['auto', 'auto', '1fr'] : ['auto', '1fr']}
          height="100%"
          width="100%"
          UNSAFE_style={{ backgroundColor: '#f5f6f9', padding: '0px', margin: '0px', overflow: 'hidden' }}
        >
          {!(deviceInfo.isTablet || deviceInfo.isIPad) && (
            <View gridArea="filter">
              {showFilter ? (
                <FilterComponent
                  onFilterChange={reloadTasks}
                  filterParams={queryParams.current}
                  filterStyle={filterStyle}
                  issueTypes={issueTypes}
                  statusList={issueStatusList}
                  viewCounts={viewCounts}
                />
              ) : (
                <Flex height="100%" justifyContent="center" alignItems="center" UNSAFE_style={{ backgroundColor: 'white' }}>
                  <Loader />
                </Flex>
              )}
            </View>
          )}
          <View width="100%" gridArea="header" UNSAFE_style={pageView === 'Full' ? {} : { height: '100%' }}>
            <HeaderComponent
              onPageViewChange={handleViewChange}
              autoChanged={pageView}
              filterParams={queryParams.current}
              onSearchChange={reloadTasks}
              fpaTypeCategory=""
              onAddNew={handleAddNew}
            />
          </View>
          {(deviceInfo.isTablet || deviceInfo.isIPad) && (
            <View gridArea="filter" UNSAFE_style={{ height: '60px', backgroundColor: 'white', borderBottom: '1px solid #e0e0e0' }}>
              <FilterComponent
                onFilterChange={reloadTasks}
                filterParams={queryParams.current}
                filterStyle={filterStyle}
                issueTypes={issueTypes}
                statusList={issueStatusList}
                viewCounts={viewCounts}
              />
            </View>
          )}
          {pageView === 'Full' ? (
            <>
              {showLoader ? (
                <Loader />
              ) : (
                <>
                  <View gridArea="card" UNSAFE_style={{ backgroundColor: '#f5f6f9', overflow: 'auto' }}>
                    {listIssueList.items.length > 0 ? (
                      <ListBox
                        autoFocus
                        id="eventCards"
                        selectionMode="single"
                        onSelectionChange={handleListRowClick}
                        marginStart="15px"
                        defaultSelectedKeys={selectedKeys}
                        selectedKeys={selectedKeys}
                        aria-label={_tf('header_text')}
                        items={listIssueList.items}
                        onLoadMore={listIssueList.items.length >= PAGE_SIZE ? listIssueList.loadMore : undefined}
                        isLoading={listIssueList.isLoading}
                        UNSAFE_style={{ ...cardStyle, marginTop: 13 }}
                      >
                        {(item: any) => (
                          <Item key={item.id} textValue={item.title}>
                            <CardComponent item={item} />
                          </Item>
                        )}
                      </ListBox>
                    ) : (
                      <View marginTop="13px">
                        <NoResultsMessage />
                      </View>
                    )}
                  </View>
                  <View
                    gridArea="detail"
                    backgroundColor="static-white"
                    borderRadius="large"
                    padding="size-100"
                    paddingTop="0"
                    marginTop="17px"
                    UNSAFE_style={{ ...detailStyle, overflow: 'hidden auto' }}
                    width="100%"
                    UNSAFE_className={`${styles.detailView} ${styles.detail_scroll_host} issue-detail-scroll-host`}
                  >
                    {showLoaderDetail ? (
                      <Loader />
                    ) : (
                      <View UNSAFE_className={styles.detailsHolder} marginStart="10px">
                        {selectedItem ? (
                          <>
                            <View
                              UNSAFE_className={styles.service_status_bar}
                              marginBottom="5px"
                              marginTop="0px"
                              UNSAFE_style={{ backgroundColor: selectedItem.entryTypeColor || selectedItem.statusColor }}
                            />
                            <View height="2px">&nbsp;</View>
                            <IssueDetailsView
                              issue={selectedItem}
                              statusList={issueStatusList}
                              users={userData}
                              onStatusChange={handleStatusChange}
                              onUpdate={handleIssueUpdate}
                              isStatusUpdating={isStatusUpdating}
                              onRefreshIssue={() => getIssueDetail(String(selectedItem?.ident || ''))}
                              onDelete={() => {
                                void handleIssueDelete();
                              }}
                            />
                          </>
                        ) : (
                          <NoResultsMessage />
                        )}
                      </View>
                    )}
                  </View>
                </>
              )}
            </>
          ) : (
            <View gridArea="detail">
              {showLoader ? (
                <Loader />
              ) : (
                <View position="relative" overflow="auto" height="100%" width="100%" UNSAFE_className={styles.service_activities}>
                  {columnsData?.length > 0 && listIssueList.items.length > 0 ? (
                    <TableView
                      UNSAFE_className="tblLayoutComponent"
                      aria-label={_tf('header_text')}
                      selectionMode="single"
                      defaultSelectedKeys={selectedKeys}
                      selectionStyle="highlight"
                      width={{ base: '100%', L: '100%', M: '1100px' }}
                      minHeight="100px"
                      maxHeight={{ base: '100vh', L: '625px' }}
                      onAction={handleTableRowClick}
                    >
                      <TableHeader columns={columnsData}>
                        {(column: any) => <Column showDivider key={column.key}>{column.name}</Column>}
                      </TableHeader>
                      <TableBody
                        onLoadMore={listIssueList.items.length > 40 ? listIssueList.loadMore : undefined}
                        items={listIssueList.items}
                        loadingState={listIssueList.loadingState}
                      >
                        {(item: any) => (
                          <Row>
                            {(columnKey) => {
                              if (columnKey === 'entryTypeName') {
                                return (
                                  <Cell>
                                    <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                                      {item?.entryTypeColor ? (
                                        <CircleFill style={{ paddingRight: '10px' }} size={'18px'} color={item.entryTypeColor} />
                                      ) : null}
                                      {item[columnKey]}
                                    </span>
                                  </Cell>
                                );
                              }
                              return <Cell>{item[columnKey]}</Cell>;
                            }}
                          </Row>
                        )}
                      </TableBody>
                    </TableView>
                  ) : (
                    <NoResultsMessage />
                  )}
                </View>
              )}
            </View>
          )}
        </Grid>
        ) : (
          <Grid
            areas={['detail']}
            columns={{ base: ['100%'], L: ['100%'] }}
            rows={['auto', '1fr']}
            height="100%"
            width="100%"
            UNSAFE_style={{ backgroundColor: '#f5f6f9', padding: '10px', margin: '0px', overflow: 'hidden' }}
          >
            {pageView === 'Full' ? (
              <>
                {showLoader ? (
                  <Loader />
                ) : (
                  <View
                    gridArea="detail"
                    backgroundColor="static-white"
                    borderRadius="large"
                    padding="size-200"
                    UNSAFE_style={{ ...detailStyle, overflow: 'hidden auto' }}
                    width="100%"
                    UNSAFE_className={`${styles.detailView} ${styles.detail_scroll_host} issue-detail-scroll-host`}
                  >
                    {showLoaderDetail ? (
                      <Loader />
                    ) : (
                      <View UNSAFE_className={styles.detailsHolder} marginStart="10px">
                        {selectedItem ? (
                          <>
                            <View
                              UNSAFE_className={styles.service_status_bar}
                              marginBottom="5px"
                              marginTop="0px"
                              UNSAFE_style={{ backgroundColor: selectedItem.entryTypeColor || selectedItem.statusColor }}
                            />
                            <View height="2px">&nbsp;</View>
                            <IssueDetailsView
                              issue={selectedItem}
                              statusList={issueStatusList}
                              users={userData}
                              onStatusChange={handleStatusChange}
                              handleBackClick={() => setShowCards(true)}
                              onUpdate={handleIssueUpdate}
                              isStatusUpdating={isStatusUpdating}
                              onRefreshIssue={() => getIssueDetail(String(selectedItem?.ident || ''))}
                              onDelete={() => {
                                void handleIssueDelete();
                              }}
                            />
                          </>
                        ) : null}
                      </View>
                    )}
                  </View>
                )}
              </>
            ) : (
              <View gridArea="detail">
                {showLoader ? (
                  <Loader />
                ) : (
                  <View position="relative" overflow="auto" height="100%" width="100%" UNSAFE_className={styles.service_activities}>
                    {columnsData?.length > 0 && listIssueList.items.length > 0 ? (
                      <TableView
                        UNSAFE_className="tblLayoutComponent"
                        aria-label={_tf('header_text')}
                        selectionMode="single"
                        defaultSelectedKeys={selectedKeys}
                        selectionStyle="highlight"
                        width={{ base: '100%', L: '100%', M: '1100px' }}
                        minHeight="100px"
                        maxHeight={{ base: '100vh', L: '625px' }}
                        onAction={handleTableRowClick}
                      >
                        <TableHeader columns={columnsData}>
                          {(column: any) => <Column showDivider key={column.key}>{column.name}</Column>}
                        </TableHeader>
                        <TableBody onLoadMore={listIssueList.items.length > 40 ? listIssueList.loadMore : undefined} items={listIssueList.items} loadingState={listIssueList.loadingState}>
                          {(item: any) => (
                            <Row>
                              {(columnKey) => {
                                if (columnKey === 'entryTypeName') {
                                  return (
                                    <Cell>
                                      <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                                        {item?.entryTypeColor ? (
                                          <CircleFill style={{ paddingRight: '10px' }} size={'18px'} color={item.entryTypeColor} />
                                        ) : null}
                                        {item[columnKey]}
                                      </span>
                                    </Cell>
                                  );
                                }
                                return <Cell>{item[columnKey]}</Cell>;
                              }}
                            </Row>
                          )}
                        </TableBody>
                      </TableView>
                    ) : (
                      <NoResultsMessage />
                    )}
                  </View>
                )}
              </View>
            )}
          </Grid>
        )
      ) : (
        <Grid areas={['detail']} columns={{ base: ['100%'], L: ['100%'] }} rows={['auto', '1fr']} height="100%" width="100%" UNSAFE_style={{ backgroundColor: '#f5f6f9', padding: '10px', margin: '0px', overflow: 'hidden' }}>
          {pageView === 'Full' ? (
            <>
              {showCards ? (
                showLoader ? (
                  <Loader />
                ) : (
                  <ListBox
                    autoFocus
                    id="eventCardsMobile"
                    selectionMode="single"
                    onSelectionChange={handleListRowClick}
                    marginStart="0px"
                    defaultSelectedKeys={selectedKeys}
                    selectedKeys={selectedKeys}
                    aria-label={_tf('header_text')}
                    items={listIssueList.items}
                    onLoadMore={listIssueList.items.length >= PAGE_SIZE ? listIssueList.loadMore : undefined}
                    isLoading={listIssueList.isLoading}
                    UNSAFE_style={{ ...cardStyle, marginTop: 13 }}
                  >
                    {(item: any) => (
                      <Item key={item.id} textValue={item.title}>
                        <CardComponentMobile item={item} />
                      </Item>
                    )}
                  </ListBox>
                )
              ) : showLoaderDetail ? (
                <Loader />
              ) : (
                <View
                  gridArea="detail"
                  backgroundColor="static-white"
                  borderRadius="large"
                  padding="size-100"
                  UNSAFE_style={{ ...detailStyle, overflow: 'hidden auto' }}
                  width="100%"
                  UNSAFE_className={`${styles.detailView} ${styles.detail_scroll_host} issue-detail-scroll-host`}
                >
                  <View UNSAFE_className={styles.detailsHolder} marginStart="0">
                    {selectedItem ? (
                      <>
                        <View
                          UNSAFE_className={styles.service_status_bar}
                          marginBottom="5px"
                          marginTop="0px"
                          UNSAFE_style={{ backgroundColor: selectedItem.entryTypeColor || selectedItem.statusColor }}
                        />
                        <View height="2px">&nbsp;</View>
                        <IssueDetailsViewMobile
                          issue={selectedItem}
                          handleBackClick={() => setShowCards(true)}
                          statusList={issueStatusList}
                          users={userData}
                          onStatusChange={handleStatusChange}
                          onUpdate={handleIssueUpdate}
                          isStatusUpdating={isStatusUpdating}
                          onRefreshIssue={() => getIssueDetail(String(selectedItem?.ident || ''))}
                          onDelete={() => {
                            void handleIssueDelete();
                          }}
                        />
                      </>
                    ) : null}
                  </View>
                </View>
              )}
            </>
          ) : (
            <View gridArea="detail">
              {showLoader ? (
                <Loader />
              ) : (
                <View position="relative" overflow="auto" height="100%" width="100%" UNSAFE_className={styles.service_activities}>
                  {columnsData?.length > 0 && listIssueList.items.length > 0 ? (
                    <TableView
                      UNSAFE_className="tblLayoutComponent"
                      aria-label={_tf('header_text')}
                      selectionMode="single"
                      defaultSelectedKeys={selectedKeys}
                      selectionStyle="highlight"
                      width={{ base: '100%', L: '100%', M: '1100px' }}
                      minHeight="100px"
                      maxHeight={{ base: '100vh', L: '625px' }}
                      onAction={handleTableRowClick}
                    >
                      <TableHeader columns={columnsData}>
                        {(column: any) => <Column showDivider key={column.key}>{column.name}</Column>}
                      </TableHeader>
                      <TableBody onLoadMore={listIssueList.items.length > 40 ? listIssueList.loadMore : undefined} items={listIssueList.items} loadingState={listIssueList.loadingState}>
                        {(item: any) => (
                          <Row>
                            {(columnKey) => {
                              if (columnKey === 'entryTypeName') {
                                return (
                                  <Cell>
                                    <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                                      {item?.entryTypeColor ? (
                                        <CircleFill style={{ paddingRight: '10px' }} size={'18px'} color={item.entryTypeColor} />
                                      ) : null}
                                      {item[columnKey]}
                                    </span>
                                  </Cell>
                                );
                              }
                              return <Cell>{item[columnKey]}</Cell>;
                            }}
                          </Row>
                        )}
                      </TableBody>
                    </TableView>
                  ) : (
                    <NoResultsMessage />
                  )}
                </View>
              )}
            </View>
          )}
        </Grid>
      )}
      <DialogContainer onDismiss={() => setShowTemplatePicker(false)}>
        {showTemplatePicker ? (
          <IssueTemplatePickerDialog
            templates={issueTemplates}
            onSelect={handleTemplateSelect}
            onClose={() => setShowTemplatePicker(false)}
          />
        ) : null}
      </DialogContainer>
      <DialogContainer onDismiss={handleAddNewClose}>
        {showAddDialog ? (
          <CreateIssueDialog
            template={selectedTemplate!}
            onClose={handleAddNewClose}
            onCreated={handleRefreshAddNew}
            issueTypes={issueTypes}
            statusList={issueStatusList}
            users={userData}
          />
        ) : null}
      </DialogContainer>
    </>
  );
}
